import { Link } from 'react-router-dom';
import { Link as LinkIcon } from 'lucide-react';
import personaBuilderData from '../../../../data/personaBuilder/data.json';
import { PERSONA_GENDER_OPTIONS } from '../../../../data/personaBuilder/data';
import { Input } from '../../input';
import { Textarea } from '../../textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '../../select';
import { Button } from '../../button/button';
import InputFieldLabel from '../../input-field-label';
import Accordion from '../../accordion/accordion';
import type { PersonaFormProps } from './types';
import { DescriptionBox } from '../../description-box';
import {
  MODEL_SELECTION_TOOLTIP,
  TRIAL_DISABLED_TOOLTIP,
} from '../../../../lib/trial-model';
import { SectorSelect } from '../../sector-select/sector-select';

const SELECT_NO_VALUE = 'none';

export function PersonaForm({
  values,
  onChange,
  editingPersona,
  isSaving,
  canSave = true,
  onCancel,
  onSave,
  onSaveDraft,
  aiModels,
  isTemplate,
  sectorId,
  onSectorChange,
}: PersonaFormProps) {
  return (
    <div className="space-y-6">
      <DescriptionBox text={personaBuilderData.introText.text} />
      <p className="!mt-0 p-0 text-sm italic text-prompt-safecomabbey">
        {personaBuilderData.introText.allFieldsOptional}
      </p>
      <Accordion
        openByDefault
        title={personaBuilderData.accordions.basicInformation.title}
      >
        <div className="flex flex-col gap-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <InputFieldLabel
                label={personaBuilderData.name.label}
                tooltip={personaBuilderData.name.tooltip}
              />
              <Input
                value={values.name}
                onChange={(e) => onChange('name', e.target.value)}
                placeholder={personaBuilderData.name.placeholder}
                className="bg-prompt-safecomwhite"
              />
            </div>
            <div className="space-y-2">
              <InputFieldLabel
                label={personaBuilderData.shortTag.label}
                tooltip={personaBuilderData.shortTag.tooltip}
              />
              <Input
                value={values.shortTag}
                onChange={(e) => onChange('shortTag', e.target.value)}
                placeholder={personaBuilderData.shortTag.placeholder}
                className="bg-prompt-safecomwhite"
                maxLength={25}
              />
            </div>
          </div>
          <div className="space-y-2">
            <InputFieldLabel
              label={personaBuilderData.model.label}
              tooltip={`${personaBuilderData.model.tooltip} ${MODEL_SELECTION_TOOLTIP}`}
            />
            <Select
              value={values.selectedAiModel || ''}
              onValueChange={(v) => onChange('selectedAiModel', v)}
            >
              <SelectTrigger className="bg-prompt-safecomwhite">
                <SelectValue
                  placeholder={personaBuilderData.model.placeholder}
                />
              </SelectTrigger>
              <SelectContent>
                {aiModels.map((model) => (
                  <SelectItem
                    key={model.id}
                    value={model.id}
                    disabled={!model.enabled}
                    title={!model.enabled ? TRIAL_DISABLED_TOOLTIP : undefined}
                  >
                    <>
                      {model.name}
                      {!model.enabled && (
                        <span className="absolute right-8 text-xs italic text-muted-foreground">
                          Coming soon
                        </span>
                      )}
                    </>
                  </SelectItem>
                ))}
                <SelectSeparator />
                <SelectItem
                  value="add-models"
                  disabled
                  title={TRIAL_DISABLED_TOOLTIP}
                >
                  <>
                    Add models
                    <span className="absolute right-8 text-xs italic text-muted-foreground">
                      Coming soon
                    </span>
                  </>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          {isTemplate && onSectorChange && (
            <SectorSelect value={sectorId ?? null} onChange={onSectorChange} />
          )}
          {editingPersona?.hidden_details &&
            editingPersona.target_evaluator_name && (
              <div className="space-y-2">
                <InputFieldLabel
                  label="Target Evaluator"
                  tooltip="The evaluator this adversarial persona was generated from."
                />
                <div className="flex items-center gap-2">
                  <span className="text-sm">
                    {editingPersona.target_evaluator_name}
                  </span>
                  <Link
                    to={`/evaluator-builder/${editingPersona.target_evaluator}`}
                    title="Go to evaluator"
                  >
                    <LinkIcon className="h-4 w-4 text-prompt-safecomoutrageous-orange" />
                  </Link>
                </div>
              </div>
            )}
        </div>
      </Accordion>
      {!editingPersona?.hidden_details && (
        <>
          <Accordion
            title={personaBuilderData.accordions.backgroundAndIdentity.title}
          >
            <DescriptionBox
              text={
                personaBuilderData.accordions.backgroundAndIdentity.description
              }
            />
            <div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <InputFieldLabel
                    label={personaBuilderData.ageBracket.label}
                    tooltip={personaBuilderData.ageBracket.tooltip}
                  />
                  <Input
                    value={values.ageBracket}
                    onChange={(e) => onChange('ageBracket', e.target.value)}
                    placeholder={personaBuilderData.ageBracket.placeholder}
                    className="bg-prompt-safecomwhite"
                  />
                </div>
                <div className="space-y-2">
                  <InputFieldLabel
                    label={personaBuilderData.socioEconomicStatus.label}
                    tooltip={personaBuilderData.socioEconomicStatus.tooltip}
                  />
                  <Select
                    value={values.socioEconomicStatus}
                    onValueChange={(v) => onChange('socioEconomicStatus', v)}
                  >
                    <SelectTrigger className="bg-prompt-safecomwhite">
                      <SelectValue
                        placeholder={
                          personaBuilderData.socioEconomicStatus.placeholder
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={SELECT_NO_VALUE}>
                        {personaBuilderData.notSelected}
                      </SelectItem>
                      {personaBuilderData.socioEconomicStatus.options.map(
                        (option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <InputFieldLabel
                    label={personaBuilderData.profession.label}
                    tooltip={personaBuilderData.profession.tooltip}
                  />
                  <Input
                    value={values.profession}
                    onChange={(e) => onChange('profession', e.target.value)}
                    placeholder={personaBuilderData.profession.placeholder}
                    className="bg-prompt-safecomwhite"
                  />
                </div>
                <div className="space-y-2">
                  <InputFieldLabel
                    label={personaBuilderData.englishDialect.label}
                    tooltip={personaBuilderData.englishDialect.tooltip}
                  />
                  <Select
                    value={values.englishDialect}
                    onValueChange={(v) => onChange('englishDialect', v)}
                  >
                    <SelectTrigger className="bg-prompt-safecomwhite">
                      <SelectValue
                        placeholder={
                          personaBuilderData.englishDialect.placeholder
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={SELECT_NO_VALUE}>
                        {personaBuilderData.notSelected}
                      </SelectItem>
                      {personaBuilderData.englishDialect.options.map(
                        (option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <InputFieldLabel
                    label={personaBuilderData.gender.label}
                    tooltip={personaBuilderData.gender.tooltip}
                  />
                  <Select
                    value={values.gender}
                    onValueChange={(v) => onChange('gender', v)}
                  >
                    <SelectTrigger className="bg-prompt-safecomwhite">
                      <SelectValue
                        placeholder={personaBuilderData.gender.placeholder}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={SELECT_NO_VALUE}>
                        {personaBuilderData.notSelected}
                      </SelectItem>
                      {PERSONA_GENDER_OPTIONS?.map((option) => (
                        <SelectItem key={option?.value} value={option?.value}>
                          {option?.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </Accordion>
          <Accordion
            title={
              personaBuilderData.accordions.beliefsAndBehaviouralOrientation
                .title
            }
          >
            <DescriptionBox
              text={
                personaBuilderData.accordions.beliefsAndBehaviouralOrientation
                  .description
              }
            />
            <div>
              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <InputFieldLabel
                      label={personaBuilderData.habitChangeKnowledge.label}
                      tooltip={personaBuilderData.habitChangeKnowledge.tooltip}
                    />
                    <Select
                      value={values.habitChangeKnowledge}
                      onValueChange={(v) => onChange('habitChangeKnowledge', v)}
                    >
                      <SelectTrigger className="bg-prompt-safecomwhite">
                        <SelectValue
                          placeholder={
                            personaBuilderData.habitChangeKnowledge.placeholder
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={SELECT_NO_VALUE}>
                          {personaBuilderData.notSelected}
                        </SelectItem>
                        {personaBuilderData.habitChangeKnowledge.options.map(
                          (option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <InputFieldLabel
                      label={personaBuilderData.selfEfficacy.label}
                      tooltip={personaBuilderData.selfEfficacy.tooltip}
                    />
                    <Select
                      value={values.selfEfficacy}
                      onValueChange={(v) => onChange('selfEfficacy', v)}
                    >
                      <SelectTrigger className="bg-prompt-safecomwhite">
                        <SelectValue
                          placeholder={
                            personaBuilderData.selfEfficacy.placeholder
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={SELECT_NO_VALUE}>
                          {personaBuilderData.notSelected}
                        </SelectItem>
                        {personaBuilderData.selfEfficacy.options.map(
                          (option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <InputFieldLabel
                      label={personaBuilderData.locusOfControl.label}
                      tooltip={personaBuilderData.locusOfControl.tooltip}
                    />
                    <Select
                      value={values.locusOfControl}
                      onValueChange={(v) => onChange('locusOfControl', v)}
                    >
                      <SelectTrigger className="bg-prompt-safecomwhite">
                        <SelectValue
                          placeholder={
                            personaBuilderData.locusOfControl.placeholder
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={SELECT_NO_VALUE}>
                          {personaBuilderData.notSelected}
                        </SelectItem>
                        {personaBuilderData.locusOfControl.options.map(
                          (option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <InputFieldLabel
                      label={personaBuilderData.mindsetFlexibility.label}
                      tooltip={personaBuilderData.mindsetFlexibility.tooltip}
                    />
                    <Select
                      value={values.mindsetFlexibility}
                      onValueChange={(v) => onChange('mindsetFlexibility', v)}
                    >
                      <SelectTrigger className="bg-prompt-safecomwhite">
                        <SelectValue
                          placeholder={
                            personaBuilderData.mindsetFlexibility.placeholder
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={SELECT_NO_VALUE}>
                          {personaBuilderData.notSelected}
                        </SelectItem>
                        {personaBuilderData.mindsetFlexibility.options.map(
                          (option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          </Accordion>

          <Accordion
            title={personaBuilderData.accordions.agentInteractions.title}
          >
            <DescriptionBox
              text={personaBuilderData.accordions.agentInteractions.description}
            />
            <div className="flex flex-col gap-4">
              <div className="space-y-2">
                <InputFieldLabel
                  label={personaBuilderData.reasonsForChattingWithBot.label}
                  tooltip={personaBuilderData.reasonsForChattingWithBot.tooltip}
                />
                <Textarea
                  value={values.reasonsForChatting}
                  onChange={(e) =>
                    onChange('reasonsForChatting', e.target.value)
                  }
                  placeholder={
                    personaBuilderData.reasonsForChattingWithBot.placeholder
                  }
                  className="bg-prompt-safecomwhite"
                />
              </div>
              <div className="space-y-2">
                <InputFieldLabel
                  label={personaBuilderData.interactionDirections.label}
                  tooltip={personaBuilderData.interactionDirections.tooltip}
                />
                <Textarea
                  value={values.interactionDirections}
                  onChange={(e) =>
                    onChange('interactionDirections', e.target.value)
                  }
                  placeholder={
                    personaBuilderData.interactionDirections.placeholder
                  }
                  className="bg-prompt-safecomwhite"
                />
              </div>
            </div>
          </Accordion>
          <Accordion
            title={
              personaBuilderData.accordions.healthRelatedBehavioursAndHistory
                .title
            }
          >
            <DescriptionBox
              text={
                personaBuilderData.accordions.healthRelatedBehavioursAndHistory
                  .description
              }
            />
            <div>
              <div className="space-y-4">
                <div className="grid gap-4 2xl:grid-cols-2">
                  <div className="space-y-2">
                    <InputFieldLabel
                      label={personaBuilderData.qualityOfSleep.label}
                      tooltip={personaBuilderData.qualityOfSleep.tooltip}
                    />
                    <Select
                      value={values.qualityOfSleep}
                      onValueChange={(v) => onChange('qualityOfSleep', v)}
                    >
                      <SelectTrigger className="bg-prompt-safecomwhite">
                        <SelectValue
                          placeholder={
                            personaBuilderData.qualityOfSleep.placeholder
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={SELECT_NO_VALUE}>
                          {personaBuilderData.notSelected}
                        </SelectItem>
                        {personaBuilderData.qualityOfSleep.options.map(
                          (option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <InputFieldLabel
                      label={personaBuilderData.levelOfEngagement.label}
                      tooltip={personaBuilderData.levelOfEngagement.tooltip}
                    />
                    <Select
                      value={values.levelOfEngagement}
                      onValueChange={(v) => onChange('levelOfEngagement', v)}
                    >
                      <SelectTrigger className="bg-prompt-safecomwhite">
                        <SelectValue
                          placeholder={
                            personaBuilderData.levelOfEngagement.placeholder
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={SELECT_NO_VALUE}>
                          {personaBuilderData.notSelected}
                        </SelectItem>
                        {personaBuilderData.levelOfEngagement.options.map(
                          (option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <InputFieldLabel
                      label={personaBuilderData.eatingControl.label}
                      tooltip={personaBuilderData.eatingControl.tooltip}
                    />
                    <Select
                      value={values.eatingControl}
                      onValueChange={(v) => onChange('eatingControl', v)}
                    >
                      <SelectTrigger className="bg-prompt-safecomwhite">
                        <SelectValue
                          placeholder={
                            personaBuilderData.eatingControl.placeholder
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={SELECT_NO_VALUE}>
                          {personaBuilderData.notSelected}
                        </SelectItem>
                        {personaBuilderData.eatingControl.options.map(
                          (option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <InputFieldLabel
                      label={personaBuilderData.emotionalRegulation.label}
                      tooltip={personaBuilderData.emotionalRegulation.tooltip}
                    />
                    <Select
                      value={values.emotionalRegulation}
                      onValueChange={(v) => onChange('emotionalRegulation', v)}
                    >
                      <SelectTrigger className="bg-prompt-safecomwhite">
                        <SelectValue
                          placeholder={
                            personaBuilderData.emotionalRegulation.placeholder
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={SELECT_NO_VALUE}>
                          {personaBuilderData.notSelected}
                        </SelectItem>
                        {personaBuilderData.emotionalRegulation.options.map(
                          (option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <InputFieldLabel
                    label={personaBuilderData.weight.label}
                    tooltip={personaBuilderData.weight.tooltip}
                  />
                  <Input
                    value={values.weight}
                    onChange={(e) => onChange('weight', e.target.value)}
                    placeholder={personaBuilderData.weight.placeholder}
                    className="bg-prompt-safecomwhite"
                  />
                </div>
                <div className="space-y-2">
                  <InputFieldLabel
                    label={personaBuilderData.pastWeightlossInterventions.label}
                    tooltip={
                      personaBuilderData.pastWeightlossInterventions.tooltip
                    }
                  />
                  <Textarea
                    value={values.pastWeightlossInterventions}
                    onChange={(e) =>
                      onChange('pastWeightlossInterventions', e.target.value)
                    }
                    placeholder={
                      personaBuilderData.pastWeightlossInterventions.placeholder
                    }
                    className="bg-prompt-safecomwhite"
                  />
                </div>
                <div className="grid gap-4 xl:grid-cols-2">
                  <div className="space-y-2">
                    <InputFieldLabel
                      label={personaBuilderData.mentalWellbeing.label}
                      tooltip={personaBuilderData.mentalWellbeing.tooltip}
                    />
                    <Textarea
                      value={values.mentalWellbeing}
                      onChange={(e) =>
                        onChange('mentalWellbeing', e.target.value)
                      }
                      placeholder={
                        personaBuilderData.mentalWellbeing.placeholder
                      }
                      className="bg-prompt-safecomwhite"
                    />
                  </div>
                  <div className="space-y-2">
                    <InputFieldLabel
                      label={personaBuilderData.neurodiversity.label}
                      tooltip={personaBuilderData.neurodiversity.tooltip}
                    />
                    <Textarea
                      value={values.neurodiversity}
                      onChange={(e) =>
                        onChange('neurodiversity', e.target.value)
                      }
                      placeholder={
                        personaBuilderData.neurodiversity.placeholder
                      }
                      className="bg-prompt-safecomwhite"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <InputFieldLabel
                    label={personaBuilderData.physicalHealthCondition.label}
                    tooltip={personaBuilderData.physicalHealthCondition.tooltip}
                  />
                  <Textarea
                    value={values.physicalHealthCondition}
                    onChange={(e) =>
                      onChange('physicalHealthCondition', e.target.value)
                    }
                    placeholder={
                      personaBuilderData.physicalHealthCondition.placeholder
                    }
                    className="bg-prompt-safecomwhite"
                  />
                </div>
                <div className="grid gap-4 md:grid-cols-2"></div>
                <div className="space-y-2">
                  <InputFieldLabel
                    label={personaBuilderData.timeResourcesEnvironment.label}
                    tooltip={
                      personaBuilderData.timeResourcesEnvironment.tooltip
                    }
                  />
                  <Textarea
                    value={values.supportiveEnvironment}
                    onChange={(e) =>
                      onChange('supportiveEnvironment', e.target.value)
                    }
                    placeholder={
                      personaBuilderData.timeResourcesEnvironment.placeholder
                    }
                    className="bg-prompt-safecomwhite"
                  />
                </div>
              </div>
            </div>
          </Accordion>

          <Accordion
            openByDefault
            title={personaBuilderData.accordions.additionalContext.title}
          >
            <DescriptionBox
              text={personaBuilderData.accordions.additionalContext.description}
            />
            <div className="space-y-2">
              <InputFieldLabel
                label={personaBuilderData.customField.label}
                tooltip={personaBuilderData.customField.tooltip}
              />
              <Textarea
                value={values.customField}
                onChange={(e) => onChange('customField', e.target.value)}
                placeholder={personaBuilderData.customField.placeholder}
                className="bg-prompt-safecomwhite"
              />
            </div>
          </Accordion>
        </>
      )}

      <div className="flex justify-end gap-2 pt-4">
        {editingPersona && (
          <Button
            onClick={onCancel}
            variant="outline"
            className="rounded-[50px] border-prompt-safecomoutrageous-orange px-8 text-prompt-safecomoutrageous-orange hover:bg-prompt-safecomfantasy hover:text-prompt-safecomoutrageous-orange"
            disabled={isSaving}
          >
            {personaBuilderData.buttons.cancel}
          </Button>
        )}
        {editingPersona?.status !== 'published' && (
          <Button
            onClick={onSaveDraft}
            variant="outline"
            className="rounded-[50px] border-prompt-safecomabbey px-8 text-prompt-safecomabbey hover:bg-prompt-safecomfantasy"
            disabled={isSaving || !canSave}
          >
            {isSaving
              ? personaBuilderData.buttons.savingDraft
              : personaBuilderData.buttons.saveDraft}
          </Button>
        )}
        <Button
          data-onboarding="persona-save"
          onClick={onSave}
          className="hover:bg-prompt-safecomoutrageous-orange/90 rounded-[50px] bg-prompt-safecomoutrageous-orange px-8 text-prompt-safecomwhite"
          disabled={isSaving || !canSave}
        >
          {isSaving
            ? personaBuilderData.buttons.saving
            : editingPersona?.status !== 'published'
              ? personaBuilderData.buttons.publish
              : personaBuilderData.buttons.updatePersona}
        </Button>
      </div>
    </div>
  );
}
